import React from "react";
import { Link } from "react-router-dom";
import {
  Clock,
  ArrowSquareOut,
  CaretDown,
  CaretUp,
  CircleNotch,
  ChatCircle,
  Trash,
  PaperPlaneRight
} from "@phosphor-icons/react";

export function NewsArticleCard({ 
  article, 
  isExpanded, 
  comments, 
  loadingComments,
  onToggle, 
  onAddComment,
  onDeleteComment,
  newComment, 
  onCommentChange,
  submitting,
  user,
  formatDate 
}) {
  const canDeleteComment = (comment) => {
    if (!user) return false;
    return comment.user_id === user.id || user.role === "admin";
  };

  return (
    <div className="bg-white border border-[#E5E7EB] overflow-hidden">
      {/* Article Header - Always Visible */}
      <div className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <span className="px-2 py-0.5 bg-[#0F766E] text-white text-[10px] font-mono uppercase">
                {article.region || "news"}
              </span>
              <span className="text-xs text-[#6B7280]">{article.source}</span>
            </div>
            <Link 
              to={`/article/${article.id}`}
              className="font-serif text-lg text-[#111827] hover:text-[#0F766E] transition-colors line-clamp-2"
            >
              {article.title}
            </Link>
            <p className="text-sm text-[#4B5563] mt-1 line-clamp-2">{article.summary}</p>
            <div className="flex items-center gap-3 mt-2 text-xs text-[#6B7280]">
              <span className="flex items-center gap-1">
                <Clock size={12} />
                {formatDate(article.published_at)}
              </span>
              <Link 
                to={`/article/${article.id}`}
                className="flex items-center gap-1 text-[#0F766E] hover:underline"
              >
                Read full article
                <ArrowSquareOut size={12} />
              </Link>
            </div>
          </div>
        </div>
        
        {/* Expand/Collapse Button */}
        <button
          onClick={onToggle}
          className="mt-3 flex items-center gap-2 text-sm text-[#0F766E] hover:text-[#0D6B64] font-medium transition-colors"
          data-testid={`discuss-${article.id}`}
        >
          <ChatCircle size={16} />
          {isExpanded ? "Hide Discussion" : `Discuss (${comments?.length || 0})`}
          {isExpanded ? <CaretUp size={14} /> : <CaretDown size={14} />}
        </button>
      </div>
      
      {/* Collapsible Comments Section */}
      {isExpanded && (
        <div className="border-t border-[#E5E7EB] bg-[#F9FAFB]">
          {/* Add Comment Form */}
          {user ? (
            <div className="p-4 border-b border-[#E5E7EB]">
              <div className="flex gap-3">
                <div className="w-8 h-8 bg-[#0F766E] text-white flex items-center justify-center text-sm font-medium flex-shrink-0">
                  {user.name?.charAt(0).toUpperCase() || "U"}
                </div>
                <div className="flex-1">
                  <textarea
                    placeholder="Share your thoughts on this article..."
                    value={newComment}
                    onChange={(e) => onCommentChange(e.target.value)}
                    rows={2}
                    className="w-full px-3 py-2 border border-[#E5E7EB] text-sm resize-none focus:outline-none focus:border-[#0F766E]"
                  />
                  <button
                    onClick={onAddComment}
                    disabled={submitting || !newComment.trim()}
                    className="mt-2 px-4 py-1.5 bg-[#0F766E] text-white text-sm hover:bg-[#14B8A6] disabled:opacity-50 flex items-center gap-2"
                  >
                    {submitting ? <CircleNotch size={14} className="animate-spin" /> : <PaperPlaneRight size={14} />}
                    Post
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="p-4 border-b border-[#E5E7EB] text-center text-sm text-[#6B7280]">
              <Link to="/login" className="text-[#0F766E] hover:underline">Sign in</Link> to join the discussion
            </div>
          )}
          
          {/* Comments List */}
          <div className="max-h-[400px] overflow-y-auto">
            {loadingComments ? (
              <div className="p-8 text-center">
                <CircleNotch size={24} className="animate-spin text-[#0F766E] mx-auto" />
              </div>
            ) : comments?.length === 0 ? (
              <div className="p-8 text-center text-sm text-[#6B7280]">
                No comments yet. Be the first to share your thoughts!
              </div>
            ) : (
              <div className="divide-y divide-[#E5E7EB]">
                {comments?.map((comment) => (
                  <div key={comment.id} className="p-4">
                    <div className="flex gap-3">
                      <div className="w-8 h-8 bg-[#E5E7EB] text-[#4B5563] flex items-center justify-center text-sm font-medium flex-shrink-0">
                        {comment.user_name?.charAt(0).toUpperCase() || "?"}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-[#111827]">{comment.user_name}</span>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-[#6B7280]">{formatDate(comment.created_at)}</span>
                            {canDeleteComment(comment) && (
                              <button
                                onClick={() => onDeleteComment(comment.id)}
                                className="text-[#6B7280] hover:text-[#DC2626] transition-colors"
                                title="Delete comment"
                              >
                                <Trash size={14} />
                              </button>
                            )}
                          </div>
                        </div>
                        <p className="text-sm text-[#4B5563] mt-1">{comment.content}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default NewsArticleCard;
